import { useEffect, useState } from "react";
import {
  Box,
  Icon,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { usuarioService } from "./service/usuario.service";
import { formatarCpfCnpj, toSlug } from "../../shared/utils";

export function ListagemUsuarios() {
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [busca, setBusca] = useState("");
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const theme = useTheme();
  const mb = false;

  const buscaDebounce = useDebounce(query || "", 500);

  useEffect(() => {
    const normalizarCPF = (valor: string) => valor.replace(/[^\d]/g, "");
    const gerarQuery = () => {
      if (!busca || busca.trim() === "") return undefined;

      const buscaLimpa = normalizarCPF(busca);
      const isNumero = /^\d+$/.test(buscaLimpa);

      if (isNumero && buscaLimpa.length >= 11) {
        return `campo_pesquisa=cpf&tipo_pesquisa=in&valor_pesquisa=${buscaLimpa}`;
      }

      return `campo_pesquisa=slug&tipo_pesquisa=in&valor_pesquisa=${toSlug(
        busca
      )}`;
    };

    setQuery(gerarQuery());
  }, [busca]);

  const carregarUsuarios = async (
    query?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      const result = await usuarioService.findAll(query, pagina, limite);
      setUsuarios(result.items || []);
      setTotalCount(result.totalItems || 0);
      setIsLoading(false);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      setUsuarios([]);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    carregarUsuarios(query, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarUsuarios(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarUsuarios(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir este usuário?")) {
      try {
        const usuarioDeletado = await usuarioService.deletar(id);
        toast.success(usuarioDeletado);
        carregarUsuarios(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };

  return (
    <BaseLayout
      title="Listagem de Usuários"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por nome ou CPF"}
          textoBotaoNovo="Adicionar novo Usuário"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() => navigate("/usuario/criar")}
        />
      }
    >
      <Box
        display={mb ? "none" : "flex"}
        flexDirection="column"
        paddingBottom="40px"
        sx={{ maxHeight: 420, minHeight: 420 }}
      >
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
        <TabelaDaListagem
          ariaLabel="tabela de usuários"
          headTable={["Nome", "E-mail", "CPF", "Ações"]}
          tableBody={
            usuarios.length > 0 ? (
              usuarios.map((usuario) => (
                <TableRow hover key={usuario.id}>
                  <TableCell>{usuario.fullName}</TableCell>
                  <TableCell>{usuario.email}</TableCell>
                  <TableCell>{formatarCpfCnpj(usuario.cpf)}</TableCell>
                  <TableCell>
                    <Tooltip title="Editar usuário">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/usuario/detalhe/${usuario.id}`)
                        }
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir usuário">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(usuario.id)}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum usuário encontrado :(
                </TableCell>
              </TableRow>
            )
          }
        />
      </Box>
    </BaseLayout>
  );
}
